import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CrmRoleResponse } from '../models/user.model';
import {
  CrmPageResponse,
  CrmRegisterRequest,
  CrmUpdateUserRequest,
  CrmUserResponse
} from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly api = `${environment.apiUrl}api/auth`;

  constructor(private http: HttpClient) {}

  // Manager+
  searchUsers(params: {
    businessId: number;
    isActive?: boolean | null;
    roleId?: number | null;
    page?: number;
    size?: number;
  }): Observable<CrmPageResponse<CrmUserResponse>> {
    let hp = new HttpParams()
      .set('businessId', String(params.businessId))
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 20));

    if (params.isActive !== undefined && params.isActive !== null) {
      hp = hp.set('isActive', String(params.isActive));
    }
    if (params.roleId !== undefined && params.roleId !== null) {
      hp = hp.set('roleId', String(params.roleId));
    }

    return this.http.get<CrmPageResponse<CrmUserResponse>>(`${this.api}/users/search`, { params: hp });
  }

  getById(id: number): Observable<CrmUserResponse> {
    return this.http.get<CrmUserResponse>(`${this.api}/users/${id}`);
  }

  // GET /api/auth/users/selectable?businessId — lista usuarios activos del negocio
  // para selectores/menciones. Endpoint liviano accesible a cualquier usuario del
  // negocio (sin permiso del módulo "users"); el backend NO busca por texto → filtrar client-side.
  listActiveByBusiness(businessId: number): Observable<CrmUserResponse[]> {
    const params = new HttpParams().set('businessId', String(businessId));
    return this.http.get<CrmUserResponse[]>(`${this.api}/users/selectable`, { params });
  }

  // Crear (usa /register)
  create(request: CrmRegisterRequest): Observable<CrmUserResponse> {
    return this.http.post<CrmUserResponse>(`${this.api}/register`, request);
  }

  // Super Admin
  updateAsSuperAdmin(id: number, request: CrmUpdateUserRequest): Observable<CrmUserResponse> {
    return this.http.put<CrmUserResponse>(`${this.api}/admin/users/${id}`, request);
  }

  // Super Admin (204)
  deactivateAsSuperAdmin(id: number): Observable<void> {
    return this.http.delete<void>(`${this.api}/admin/users/${id}`);
  }

  // Super Admin
  activateAsSuperAdmin(id: number): Observable<CrmUserResponse> {
    return this.http.put<CrmUserResponse>(`${this.api}/admin/users/${id}/activate`, {});
  }
    // Admin / Super Admin
  resetPasswordAsAdmin(userId: number, newPassword: string): Observable<void> {
    const body = { newPassword };
    return this.http.put<void>(`${this.api}/admin/users/${userId}/password`, body);
  }

  // Admin / Super Admin — INC_CRM_022 inc5: envía al usuario un enlace seguro de
  // restablecimiento con expiración para que él mismo defina su nueva contraseña.
  sendResetLinkAsAdmin(userId: number): Observable<void> {
    return this.http.post<void>(`${this.api}/admin/users/${userId}/send-reset-link`, {});
  }
  deleteUserDataAsSuperAdmin(id: number, password: string): Observable<void> {
    return this.http.delete<void>(`${this.api}/admin/users/${id}/data`, {
      body: { password }
    });
  }

  private readonly apiBase = `${environment.apiUrl}api`;

  getAssignableRoles(): Observable<CrmRoleResponse[]> {
    return this.http.get<CrmRoleResponse[]>(`${this.apiBase}/roles`);
  }

  getBusinesses(page = 0, size = 1000): Observable<CrmPageResponse<any>> {
  let hp = new HttpParams()
    .set('page', String(page))
    .set('size', String(size));
  return this.http.get<CrmPageResponse<any>>(`${this.apiBase}/businesses`, { params: hp });
}
}